import { IconX } from '@developerz/ui';
import { createSignal, createUniqueId, For, type JSX, onMount, Show } from 'solid-js';
import { t } from '../../i18n';
import type { UtilBox } from '../../lib/fleetUtilization';
import { standingRefusal } from '../../lib/move-copy';
import type { MovePage } from '../../lib/move-page';
import { queryHits, SEARCH_AT, searchOptions, searchVisible } from '../../lib/move-to-menu';
import { createMoveWork, type WorkTarget } from '../../lib/move-work-controller';
import {
  itemReason,
  moveBudget,
  movePanelFooter,
  moveWorkGoLabel,
  moveWorkWhy,
  type PanelItem,
} from '../../lib/move-work-panel';
import { buttonClass } from '../ui/Button';

// The Move work panel (#6772, epic #6552; prototype mwHTML `:1716-1750`, CSS `:1109-1141`):
// "What to move" (epics first, then jobs; an epic covers its jobs on this card), "Move to"
// (the machines that can take the selection first, then by lowest CPU), the footer naming the
// moves it uses and the moves left, the amber why line, and the Move / Cancel buttons. Every
// verdict is the server's (`lib/move-work-controller.ts`); one panel per page, opened by the Now
// header's Move, the menu's Move work or Drain machine, or the simplified card's Move work.
//
// THE MOVE BUTTON IS INK `primary`, NOT BRAND: the Fleet header's "Connect a machine" holds the
// view's one brand button whenever machines are on screen (`FleetHeader.tsx`), as Send work's
// form found (a listed deviation). Disabled, it stays focusable (`aria-disabled`) and reads
// plain "Move"; while sending, "Transferring…" with moving stripes and the ticked rows pulsing.
// Escape and Cancel close it.

/** The search boxes' shared look (Move to's, `MoveToMenu.tsx`). */
function Search(props: {
  readonly label: string;
  readonly value: string;
  readonly total: number;
  readonly shown: number;
  readonly onInput: (v: string) => void;
}): JSX.Element {
  return (
    <label class="move-menu__search">
      <input
        type="search"
        class="move-menu__input"
        placeholder={props.label}
        aria-label={props.label}
        autocomplete="off"
        spellcheck={false}
        value={props.value}
        onInput={(e) => props.onInput(e.currentTarget.value)}
      />
      <span class="move-menu__count" aria-hidden="true">
        {props.value.trim() === ''
          ? props.total
          : t('fleet.move.search_count', { shown: props.shown, total: props.total })}
      </span>
    </label>
  );
}

/** One "What to move" row: a checkbox button, its label and its right-hand detail. */
function ItemRow(props: {
  readonly item: PanelItem;
  readonly checked: boolean;
  readonly covered: boolean;
  readonly reason: string | null;
  readonly onToggle: () => void;
}): JSX.Element {
  const detail = (): string => {
    if (props.reason !== null) return props.reason;
    if (props.item.item.kind === 'epic') {
      return t.plural('fleet.machines.card_jobs', props.item.jobs, { count: props.item.jobs });
    }
    return props.covered ? t('fleet.move.work_with_epic') : '';
  };
  return (
    // biome-ignore lint/a11y/useSemanticElements: the prototype's checkbox row (`.mw-it`, `:1728`), a button that says why it is off-limits in its `title`; a native checkbox cannot wear the row.
    <button
      type="button"
      role="checkbox"
      class="move-work__item"
      classList={{ 'move-work__item--epic': props.item.item.kind === 'epic' }}
      data-move-work-item={props.item.key}
      aria-checked={props.checked}
      aria-disabled={props.reason !== null || props.covered ? 'true' : undefined}
      title={props.reason ?? (props.covered ? t('fleet.move.work_with_epic_title') : '')}
      onClick={() => props.onToggle()}
    >
      <span class="move-work__box" aria-hidden="true" />
      <span class="move-work__label">{props.item.label}</span>
      <span class="move-work__detail">{detail()}</span>
    </button>
  );
}

/** One "Move to" row: a radio button, the machine's name and 8-char id, its CPU, its refusal. */
function TargetRow(props: {
  readonly o: WorkTarget;
  readonly checked: boolean;
  readonly onPick: () => void;
}): JSX.Element {
  const cpu = (): string => {
    const c = props.o.target?.cpu ?? null;
    if (c !== null) {
      return t('fleet.move.option_cpu', {
        before: Math.round(c.before),
        after: Math.round(c.after),
      });
    }
    if (props.o.offline) return t('fleet.move.work_offline');
    if (props.o.target !== null || props.o.cpuBefore === null) return '';
    return t('fleet.move.option_cpu_now', { before: props.o.cpuBefore });
  };
  return (
    // biome-ignore lint/a11y/useSemanticElements: the prototype's radio row (`.mw-d`, `:1738`), whose refusal reads on a second line inside it; a native radio cannot hold it.
    <button
      type="button"
      role="radio"
      class="move-work__to"
      data-move-work-to={props.o.machine.id}
      aria-checked={props.checked}
      aria-disabled={props.o.canTake ? undefined : 'true'}
      onClick={() => props.onPick()}
    >
      <span class="move-work__dot" aria-hidden="true" />
      <span class="move-work__name">
        {props.o.machine.name} <span class="move-opt__id">{props.o.machine.shortId}</span>
      </span>
      <span class="move-work__detail">{cpu()}</span>
      <Show when={props.o.why}>{(why) => <small class="move-work__refusal">{why()}</small>}</Show>
    </button>
  );
}

type MoveWork = ReturnType<typeof createMoveWork>;

/** "What to move": the search at five items or more, then the checkbox rows. */
function WorkItems(props: { readonly page: MovePage; readonly w: MoveWork }): JSX.Element {
  const [q, setQ] = createSignal('');
  const shown = () => props.w.items().filter((i) => queryHits(q(), i.label));
  const reason = (it: PanelItem): string | null => {
    const r = props.w.refusalOf(it.key);
    const page = props.page;
    return r === null ? null : itemReason(r, page.clock(), t, (x) => page.textOf(x));
  };
  return (
    <>
      <p class="move-work__lab">
        {t('fleet.move.work_what')} <span>{t('fleet.move.work_what_note')}</span>
      </p>
      <Show when={searchVisible(props.w.items().length, 5, q())}>
        <Search
          label={t('fleet.move.work_search_items')}
          value={q()}
          total={props.w.items().length}
          shown={shown().length}
          onInput={setQ}
        />
      </Show>
      <fieldset class="move-work__list" aria-label={t('fleet.move.work_what')}>
        <For each={shown()}>
          {(it) => (
            <ItemRow
              item={it}
              checked={props.w.ticked().has(it.key) || props.w.covered().has(it.key)}
              covered={props.w.covered().has(it.key)}
              reason={reason(it)}
              onToggle={() => props.w.toggle(it.key)}
            />
          )}
        </For>
        <Show when={q().trim() !== '' && shown().length === 0}>
          <p class="move-menu__note">{t('fleet.move.work_items_none', { q: q().trim() })}</p>
        </Show>
      </fieldset>
    </>
  );
}

/** "Move to": the search at four machines or more, then the radio rows. */
function WorkTargets(props: { readonly w: MoveWork }): JSX.Element {
  const [q, setQ] = createSignal('');
  const shown = () => searchOptions(props.w.targets(), q());
  return (
    <>
      <p class="move-work__lab">
        {t('fleet.move.work_to')} <span>{t('fleet.move.work_to_note')}</span>
      </p>
      <Show when={searchVisible(props.w.targets().length, SEARCH_AT, q())}>
        <Search
          label={t('fleet.move.search')}
          value={q()}
          total={props.w.targets().length}
          shown={shown().length}
          onInput={setQ}
        />
      </Show>
      <div class="move-work__list" role="radiogroup" aria-label={t('fleet.move.work_to')}>
        {/* Keyed by machine id, looked up in the current list: a re-read or the clock's tick
            builds new option objects, and iterating them would drop a keyboard user's focus. */}
        <For each={shown().map((o) => o.machine.id)}>
          {(mid) => (
            <Show when={shown().find((o) => o.machine.id === mid)}>
              {(o) => (
                <TargetRow
                  o={o()}
                  checked={props.w.chosen() === mid}
                  onPick={() => {
                    if (o().canTake) props.w.choose(mid);
                  }}
                />
              )}
            </Show>
          )}
        </For>
        <Show when={q().trim() !== '' && shown().length === 0}>
          <p class="move-menu__note">{t('fleet.move.search_none', { q: q().trim() })}</p>
        </Show>
      </div>
    </>
  );
}

/** The footer, the why line and the buttons, all from the selection's preview. */
function WorkActions(props: {
  readonly page: MovePage;
  readonly box: UtilBox;
  readonly w: MoveWork;
  readonly onCancel: () => void;
}): JSX.Element {
  const w = props.w;
  const chosen = () => w.targets().find((o) => o.machine.id === w.chosen()) ?? null;
  const ready = () => {
    const p = w.selPreview();
    return p !== null && p.status === 'ready' ? p.preview : null;
  };
  const why = (): string | null => {
    if (w.sel().length === 0) return null;
    if (w.selPreview()?.status === 'failed') return t('fleet.move.preview_failed');
    const preview = ready();
    if (preview === null) return null;
    const src = standingRefusal(preview.source.refusal, props.page.clock());
    return moveWorkWhy(
      {
        blocked: props.page.blockedWhy(props.box.id),
        locked: props.page.locked(),
        refusalText: src !== null ? props.page.textOf(src) : (chosen()?.why ?? null),
        anyTarget: w.targets().some((o) => o.canTake),
      },
      t,
    );
  };
  const canGo = (): boolean =>
    w.sel().length > 0 && ready() !== null && why() === null && chosen()?.canTake === true;
  const footer = (): string | null => {
    const p = ready();
    if (p === null || w.sel().length === 0) return null;
    return movePanelFooter(
      {
        cost: p.cost,
        left: p.source.left,
        budget: moveBudget(props.box),
        name: props.box.display_name,
      },
      t,
    );
  };
  return (
    <>
      <Show when={footer()}>{(f) => <p class="move-work__foot">{f()}</p>}</Show>
      <Show when={why()}>{(text) => <p class="move-work__why">{text()}</p>}</Show>
      <div class="move-work__actions">
        <button
          type="button"
          class={`${buttonClass({ variant: 'primary', size: 'sm' })} move-work__go`}
          classList={{ 'move-work__go--sending': w.sending() }}
          aria-disabled={canGo() && !w.sending() ? undefined : 'true'}
          onClick={() => {
            if (canGo() && !w.sending()) void w.go();
          }}
        >
          {w.sending()
            ? t('fleet.move.work_sending')
            : moveWorkGoLabel(w.counts(), canGo() ? (chosen()?.machine.name ?? null) : null, t)}
        </button>
        <button
          type="button"
          class={buttonClass({ variant: 'ghost', size: 'sm' })}
          onClick={() => props.onCancel()}
        >
          {t('fleet.move.work_cancel')}
        </button>
      </div>
    </>
  );
}

export function MoveWorkPanel(props: {
  readonly page: MovePage;
  readonly box: UtilBox;
  readonly drain: boolean;
  /** Closed: by a 201 (`movedKey` names the first moved item on its new card), or by hand. */
  readonly onClose: (movedKey: string | null) => void;
}): JSX.Element {
  const id = createUniqueId();
  const w = createMoveWork(props.page, () => props.box, {
    drain: props.drain,
    onMoved: (key) => props.onClose(key),
  });
  let el: HTMLElement | undefined;
  // Focus moves into the panel (owner ruling 2026-10-20): its first item.
  onMount(() =>
    el?.querySelector<HTMLElement>('[role="checkbox"]')?.focus({ preventScroll: true }),
  );
  return (
    // biome-ignore lint/a11y/useSemanticElements: a card zone named by its heading (the prototype's `role="group"`, `:1748`), as Send work's form is; a fieldset's legend could not hold the Close button.
    <section
      ref={el}
      class="mission-zone move-work"
      classList={{ 'move-work--sending': w.sending() }}
      role="group"
      aria-labelledby={`${id}-h`}
      onKeyDown={(e) => {
        if (e.key !== 'Escape' || e.defaultPrevented) return;
        e.preventDefault();
        props.onClose(null);
      }}
    >
      <p class="mission-zone__label">
        <span id={`${id}-h`}>{t('fleet.move.work_label')}</span>
        <button
          type="button"
          class={buttonClass({ variant: 'ghost', size: 'icon-sm' })}
          aria-label={t('fleet.move.work_close')}
          title={t('fleet.move.work_close')}
          onClick={() => props.onClose(null)}
        >
          <IconX />
        </button>
      </p>
      <Show when={props.drain}>
        <p class="move-work__note">{t('fleet.move.work_drain_note')}</p>
      </Show>
      <WorkItems page={props.page} w={w} />
      <WorkTargets w={w} />
      <WorkActions page={props.page} box={props.box} w={w} onCancel={() => props.onClose(null)} />
    </section>
  );
}
